import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set } from 'firebase/database';
import { db, auth } from './firebase';
import { logAdminAction } from './auditLog';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from 'recharts';

const s = {
  card:  { backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 20 },
  input: { padding: '9px 14px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none' },
  btn:   { padding: '10px 18px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  statCard: { backgroundColor: '#FFF', borderRadius: 12, padding: 16, border: '1px solid #E5E7EB' },
};

// Same 16-region list pharmacies pick from at signup (RegisterPage.js in
// pharmdev-dashboard) — including every region here, not just ones with a
// pharmacy already, is the whole point: zero-coverage regions are exactly
// what a marketing manager needs to see to know where to focus outreach.
const REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Central',
  'Northern', 'Upper East', 'Upper West', 'Volta', 'Brong-Ahafo',
  'Oti', 'Savannah', 'North East', 'Bono East', 'Ahafo', 'Western North',
];

function buildWeeklyGrowth(pharmacies, customers, weeksBack = 10) {
  const now = new Date();
  const weeks = [];
  for (let i = weeksBack - 1; i >= 0; i--) {
    const end = new Date(now); end.setDate(end.getDate() - i * 7);
    const start = new Date(end); start.setDate(start.getDate() - 7);
    weeks.push({ start, end, label: `${start.getMonth() + 1}/${start.getDate()}` });
  }
  return weeks.map(w => ({
    week: w.label,
    Pharmacies: pharmacies.filter(p => p.createdAt && new Date(p.createdAt) >= w.start && new Date(p.createdAt) < w.end).length,
    Customers: customers.filter(u => u.createdAt && new Date(u.createdAt) >= w.start && new Date(u.createdAt) < w.end).length,
  }));
}

// ── PHARMACY UPDATES (send + history) ────────────────────────────────────
// Writes to the SAME broadcasts/{id} node CRM's "Send Broadcast" already
// uses (target: 'all_pharmacies') — this isn't a separate channel, just a
// focused, one-click front door to it that lives where a marketing manager
// actually works instead of being buried in CRM. Pharmacy dashboard's own
// Announcements tab (pharmdev-dashboard/src/AnnouncementsTab.js) already
// reads this exact node/target, so no changes were needed on that side.
function PharmacyUpdatesPanel({ pharmacyCount }) {
  const [history, setHistory] = useState([]);
  const [title, setTitle]     = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    return onValue(ref(db, 'broadcasts'), snap => {
      const all = snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : [];
      setHistory(all.filter(b => b.target === 'all_pharmacies').sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt)));
    });
  }, []);

  const send = async () => {
    if (!title.trim() || !message.trim()) return alert('Enter a title and message');
    if (!window.confirm(`Send this update to all ${pharmacyCount} onboarded pharmacies?`)) return;
    setSending(true);
    try {
      const broadcastRef = push(ref(db, 'broadcasts'));
      await set(broadcastRef, {
        title: title.trim(),
        message: message.trim(),
        target: 'all_pharmacies',
        sentAt: new Date().toISOString(),
        sentBy: auth.currentUser?.email || '',
        recipientCount: pharmacyCount,
      });
      await logAdminAction('send_pharmacy_update', title.trim(), { recipientCount: pharmacyCount });
      setTitle(''); setMessage('');
      alert(`✅ Sent — it'll show in every onboarded pharmacy's Announcements tab next time they check their dashboard.`);
    } catch (e) { alert('Error sending update'); }
    finally { setSending(false); }
  };

  return (
    <div style={s.card}>
      <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 15 }}>📣 Pharmacy Updates</h3>
      <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>
        Sends straight to every onboarded pharmacy's Announcements tab — pharmacies have no push channel yet, so
        they'll see it next time they check their dashboard, not instantly.
      </p>

      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Title</label>
      <input style={{ ...s.input, width: '100%', boxSizing: 'border-box', marginBottom: 12 }} placeholder="e.g. 🎉 New delivery-fee tiers starting Monday" value={title} onChange={e => setTitle(e.target.value)} />

      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Message</label>
      <textarea style={{ ...s.input, width: '100%', boxSizing: 'border-box', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} placeholder="What do you want pharmacies to know?" value={message} onChange={e => setMessage(e.target.value)} />

      <button style={{ ...s.btn, marginTop: 14, opacity: sending ? 0.6 : 1 }} onClick={send} disabled={sending}>
        {sending ? 'Sending...' : `📤 Send to ${pharmacyCount} onboarded pharmac${pharmacyCount === 1 ? 'y' : 'ies'}`}
      </button>

      {history.length > 0 && (
        <div style={{ marginTop: 20, borderTop: '1px solid #F0F0F0', paddingTop: 16 }}>
          <p style={{ fontWeight: 700, fontSize: 13, margin: '0 0 10px' }}>Sent History ({history.length})</p>
          {history.slice(0, 5).map(b => (
            <div key={b.id} style={{ padding: '10px 0', borderBottom: '0.5px solid #F3F4F6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>{b.title}</span>
                <span style={{ fontSize: 11, color: '#AAA' }}>{new Date(b.sentAt).toLocaleDateString()}</span>
              </div>
              <p style={{ fontSize: 12, color: '#666', margin: 0 }}>{b.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MAIN MARKETING TAB ────────────────────────────────────────────────────
// orders/pharmacies/riders/users come from App.js, which already holds one
// live subscription to each — re-subscribing here duplicated the entire
// system-wide orders/users trees a second time on every mount.
export default function MarketingTab({ orders = [], pharmacies = [], riders = [], users: customers = [] }) {
  const approvedPharmacies = pharmacies.filter(p => p.status === 'approved');
  const weeklyGrowth = buildWeeklyGrowth(pharmacies, customers);
  const regionCoverage = REGIONS.map(r => ({ region: r, count: approvedPharmacies.filter(p => p.region === r).length })).sort((a, b) => a.count - b.count);
  const uncoveredRegions = regionCoverage.filter(r => r.count === 0);

  return (
    <div>
      <h2 style={{ fontWeight: 700, marginBottom: 16 }}>📈 Marketing</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total Pharmacies', value: pharmacies.length,                             color: '#1D9E75' },
          { label: 'Approved',         value: approvedPharmacies.length,                      color: '#10B981' },
          { label: 'Regions Covered',  value: `${16 - uncoveredRegions.length}/16`,            color: '#3B82F6' },
          { label: 'Total Riders',     value: riders.length,                                  color: '#8B5CF6' },
          { label: 'Total Customers',  value: customers.length,                                color: '#F59E0B' },
          { label: 'Total Orders',     value: orders.length,                                  color: '#F97316' },
        ].map((st, i) => (
          <div key={i} style={{ ...s.statCard, borderTop: `4px solid ${st.color}` }}>
            <p style={{ fontSize: 26, fontWeight: 800, color: st.color, margin: '0 0 4px' }}>{st.value}</p>
            <p style={{ fontSize: 13, color: '#555', margin: 0 }}>{st.label}</p>
          </div>
        ))}
      </div>

      <div style={s.card}>
        <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 15 }}>📊 Weekly Growth</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>New pharmacy and customer signups per week, last 10 weeks</p>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={weeklyGrowth} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
            <XAxis dataKey="week" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="Pharmacies" stroke="#1D9E75" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Customers" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={s.card}>
        <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 15 }}>🗺️ Regional Coverage</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>
          Approved pharmacies per region, weakest first — {uncoveredRegions.length > 0
            ? `${uncoveredRegions.length} region${uncoveredRegions.length === 1 ? ' has' : 's have'} no pharmacy yet.`
            : 'every region has at least one pharmacy.'}
        </p>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={regionCoverage} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="region" width={100} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#1D9E75" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <PharmacyUpdatesPanel pharmacyCount={approvedPharmacies.length} />
    </div>
  );
}
